import React, { useState } from 'react';

import { formatDateString } from '~libs/dayjs';

import { ClickableIcon } from '~components';
import { faChevronLeft, faChevronRight } from '~components/icon';

import { CarePlanLookup, CarePlanReview } from '~clients/models';

import LookupBadge from '../goals/LookupBadge';

interface EntryProps {
  review: CarePlanReview;
  type?: CarePlanLookup;
  trigger?: CarePlanLookup;
  reviewerName?: string;
}

// One timeline card. The summary (date, badges, reviewer) is always shown; attendees,
// summary and outcome are revealed to the right when the chevron is toggled.
const ReviewTimelineEntry: React.FC<EntryProps> = ({ review, type, trigger, reviewerName }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = Boolean(review.attendees_description || review.summary || review.outcome);

  return (
    <li className="care-plan-review-entry" data-cy={`care-plan-review-entry-${review.id}`}>
      <span className="care-plan-review-dot" aria-hidden />
      <div className={`care-plan-review-card${expanded ? ' expanded' : ''}`}>
        <div className="care-plan-review-card-summary">
          <div className="care-plan-review-heading">
            <span className="fw-bold">{formatDateString(review.review_date)}</span>
            {type && <LookupBadge lookup={type} dataCy={`care-plan-review-type-${type.tag ?? type.id}`} />}
            {trigger && (
              <LookupBadge lookup={trigger} dataCy={`care-plan-review-trigger-${trigger.tag ?? trigger.id}`} />
            )}
          </div>
          {reviewerName && <div className="care-plan-review-meta">Reviewed by {reviewerName}</div>}
        </div>
        {hasDetails && (
          <div className="care-plan-review-toggle">
            <ClickableIcon
              id={`care-plan-review-toggle-${review.id}`}
              dataCy={`care-plan-review-toggle-${review.id}`}
              icon={expanded ? faChevronLeft : faChevronRight}
              size="small"
              toolTip={expanded ? 'Hide details' : 'Show details'}
              ariaLabel={expanded ? 'Hide review details' : 'Show review details'}
              onClick={() => setExpanded((open) => !open)}
            />
          </div>
        )}
        {expanded && hasDetails && (
          <div className="care-plan-review-card-details" data-cy={`care-plan-review-details-${review.id}`}>
            {review.attendees_description && (
              <div className="care-plan-review-detail">
                <span className="care-plan-review-detail-label">Attendees:</span> {review.attendees_description}
              </div>
            )}
            {review.summary && (
              <div className="care-plan-review-detail">
                <span className="care-plan-review-detail-label">Summary:</span> {review.summary}
              </div>
            )}
            {review.outcome && (
              <div className="care-plan-review-detail">
                <span className="care-plan-review-detail-label">Outcome:</span> {review.outcome}
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
};

interface ComponentProps {
  reviews: CarePlanReview[];
  reviewTypesById: Map<string, CarePlanLookup>;
  triggerTypesById: Map<string, CarePlanLookup>;
  /** TP1 user id → display name, for "reviewed by". */
  usersById: Map<number, string>;
}

/** Vertical timeline of review cards, newest first. */
const ReviewTimeline: React.FC<ComponentProps> = ({ reviews, reviewTypesById, triggerTypesById, usersById }) => {
  const ordered = [...reviews].sort((first, second) => {
    // Newest review_date first. Return 0 for equal dates (a valid total order) and
    // fall back to created_at desc as a stable tiebreaker for same-day reviews
    // (e.g. an Annual and a Triggered review recorded on the same date).
    if (first.review_date !== second.review_date) {
      return first.review_date < second.review_date ? 1 : -1;
    }
    return (second.created_at ?? '').localeCompare(first.created_at ?? '');
  });

  return (
    <ul className="care-plan-review-timeline" data-cy="care-plan-review-timeline">
      {ordered.map((review) => (
        <ReviewTimelineEntry
          key={review.id}
          review={review}
          type={review.review_type_id ? reviewTypesById.get(review.review_type_id) : undefined}
          trigger={review.trigger_type_id ? triggerTypesById.get(review.trigger_type_id) : undefined}
          reviewerName={review.reviewer_id ? usersById.get(review.reviewer_id) : undefined}
        />
      ))}
    </ul>
  );
};

export default ReviewTimeline;
